import React, { Fragment, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import type { House } from '../api';
import { useNavigate } from '@tanstack/react-router';
import { DeleteHouseModal, InviteHomeManagerModal } from './modals';
import { useAuth } from '@/app/providers';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';

interface HouseTableProps {
  data: House[];
  columns?: ColumnDef<House, any>[];
  isLoading?: boolean;
  className?: string;
}

export const HouseTable: React.FC<HouseTableProps> = ({
  data,
  columns = houseColumns,
  isLoading,
  className = '',
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true, // future-proof for API pagination
  });

  if (isLoading)
    return (
      <div className="p-4 space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-300 animate-pulse rounded" />
        ))}
      </div>
    );

  return (
    <div
      className={`min-w-3xl ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full">
        <thead className="">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="text-sm md:text-base text-center border">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-4 text-start border">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-start text-gray-400"
              >
                No houses found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ✅ Default Column Definitions (Including Actions)
const houseColumns: ColumnDef<House>[] = [
  {
    accessorKey: 'name',
    header: () => (
      <div className="p-2 md:p-4 text-sm md:text-base text-center font-semibold">House Name</div>
    ),
    cell: (info) => (
      <div className="text-sm md:text-base py-2 md:py-3">{String(info.getValue() ?? '-')}</div>
    ),
  },
  {
    accessorKey: 'address',
    header: () => <div className="p-2 md:p-4 text-sm md:text-base text-center font-semibold">Address</div>,
    cell: (info) => (
      <div className="text-sm md:text-base py-2 md:py-3">{String(info.getValue() ?? '-')}</div>
    ),
  },
  {
    accessorKey: 'capacity',
    header: () => <div className="p-2 md:p-4 text-sm md:text-base text-center font-semibold">Beds</div>,
    cell: (info) => (
      <div className="text-sm md:text-base py-2 md:py-3 text-center">{String(info.getValue() ?? '-')}</div>
    ),
  },
  {
    id: 'utilization',
    header: () => (
      <div className="p-2 md:p-4 text-sm md:text-base text-center font-semibold">Bed Utilization</div>
    ),
    cell: ({ row }) => {
      const h = row.original;
      const rate = ((h._count?.residents ?? 0) / h.capacity) * 100;
      return <div className="text-sm md:text-base py-2 md:py-3 text-center">{rate.toFixed(1)}%</div>;
    },
  },
  {
    accessorFn: (row) => row.manager?.name,
    id: 'manager',
    header: () => <div className="p-2 md:p-4 text-sm md:text-base text-center font-semibold">Manager</div>,
    cell: (info) => (
      <div className="text-sm md:text-base py-2 md:py-3">{String(info.getValue() ?? '-')}</div>
    ),
  },
  {
    accessorKey: 'status',
    header: () => <div className="p-2 md:p-4 text-sm md:text-base text-center font-semibold">Status</div>,
    cell: (info) => {
      const status = info.getValue() as 'ACTIVE' | 'INACTIVE';
      return (
        <div className="px-6 py-3 text-center">
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              status === 'ACTIVE'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {status === 'ACTIVE' ? 'Active' : 'Inactive'}
          </span>
        </div>
      );
    },
  },
  {
    id: 'actions',
    header: () => <div className="p-2 md:p-4 text-sm md:text-base font-semibold text-center">Actions</div>,
    cell: ({ row }) => <ActionsCell house={row.original} />,
  },
];

// ✅ ActionsCell component to safely use hooks
const ActionsCell: React.FC<{ house: House }> = ({ house }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleView = () => navigate({ to: `/houses/${house.id}` });
  const handleEdit = () => navigate({ to: `/houses/edit/${house.id}` });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInviteManagerModalOpen, setIsInviteManagerModalOpen] =
    useState(false);

  /** -----------------------------
   *   Floating UI positioning
   *  ----------------------------- */
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
  });

  return (
    <div className="flex justify-center">
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          className="text-gray-500 hover:text-gray-700 cursor-pointer w-5"
          ref={refs.setReference}
        >
          <svg
            width="4"
            className="mx-auto"
            height="16"
            viewBox="0 0 4 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 4C0.89543 4 -4.82823e-08 3.10457 0 2C4.82823e-08 0.89543 0.895431 -4.82823e-08 2 0C3.10457 4.82823e-08 4 0.895431 4 2C4 3.10457 3.10457 4 2 4Z"
              fill="#111827"
            />
            <path
              d="M2 10C0.89543 10 -4.82823e-08 9.10457 0 8C4.82823e-08 6.89543 0.895431 6 2 6C3.10457 6 4 6.89543 4 8C4 9.10457 3.10457 10 2 10Z"
              fill="#111827"
            />
            <path
              d="M2 16C0.89543 16 -4.82823e-08 15.1046 0 14C4.82823e-08 12.8954 0.895431 12 2 12C3.10457 12 4 12.8954 4 14C4 15.1046 3.10457 16 2 16Z"
              fill="#111827"
            />
          </svg>
        </MenuButton>

        <Transition
          as={Fragment}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-in"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <MenuItems
            ref={refs.setFloating}
            style={floatingStyles}
            className="absolute right-0 mt-2 w-50 origin-top-right bg-white border border-gray-200 shadow-lg focus:outline-none z-50"
          >
            <div className="py-1">
              <MenuItem>
                {() => (
                  <button
                    onClick={handleView}
                    className={`w-full text-sm md:text-base text-left p-2 md:p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="22"
                      height="15"
                      viewBox="0 0 22 15"
                      className="w-5 h-5"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 12C12.25 12 13.3125 11.5625 14.1875 10.6875C15.0625 9.8125 15.5 8.75 15.5 7.5C15.5 6.25 15.0625 5.1875 14.1875 4.3125C13.3125 3.4375 12.25 3 11 3C9.75 3 8.6875 3.4375 7.8125 4.3125C6.9375 5.1875 6.5 6.25 6.5 7.5C6.5 8.75 6.9375 9.8125 7.8125 10.6875C8.6875 11.5625 9.75 12 11 12ZM11 10.2C10.25 10.2 9.6125 9.9375 9.0875 9.4125C8.5625 8.8875 8.3 8.25 8.3 7.5C8.3 6.75 8.5625 6.1125 9.0875 5.5875C9.6125 5.0625 10.25 4.8 11 4.8C11.75 4.8 12.3875 5.0625 12.9125 5.5875C13.4375 6.1125 13.7 6.75 13.7 7.5C13.7 8.25 13.4375 8.8875 12.9125 9.4125C12.3875 9.9375 11.75 10.2 11 10.2ZM11 15C8.56667 15 6.35 14.3208 4.35 12.9625C2.35 11.6042 0.9 9.78333 0 7.5C0.9 5.21667 2.35 3.39583 4.35 2.0375C6.35 0.679167 8.56667 0 11 0C13.4333 0 15.65 0.679167 17.65 2.0375C19.65 3.39583 21.1 5.21667 22 7.5C21.1 9.78333 19.65 11.6042 17.65 12.9625C15.65 14.3208 13.4333 15 11 15ZM11 13C12.8833 13 14.6125 12.5042 16.1875 11.5125C17.7625 10.5208 18.9667 9.18333 19.8 7.5C18.9667 5.81667 17.7625 4.47917 16.1875 3.4875C14.6125 2.49583 12.8833 2 11 2C9.11667 2 7.3875 2.49583 5.8125 3.4875C4.2375 4.47917 3.03333 5.81667 2.2 7.5C3.03333 9.18333 4.2375 10.5208 5.8125 11.5125C7.3875 12.5042 9.11667 13 11 13Z"
                        fill="#474747"
                      />
                    </svg>
                    <span>View Details</span>
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {() => (
                  <button
                    onClick={handleEdit}
                    className={`w-full text-sm md:text-base text-left p-2 md:p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200`}
                  >
                    <svg
                      width="18"
                      height="18"
                      className="w-5 h-5"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 16H3.425L13.2 6.225L11.775 4.8L2 14.575V16ZM0 18V13.75L13.2 0.575C13.4 0.391667 13.6208 0.25 13.8625 0.15C14.1042 0.05 14.3583 0 14.625 0C14.8917 0 15.15 0.05 15.4 0.15C15.65 0.25 15.8667 0.4 16.05 0.6L17.425 2C17.625 2.18333 17.7708 2.4 17.8625 2.65C17.9542 2.9 18 3.15 18 3.4C18 3.66667 17.9542 3.92083 17.8625 4.1625C17.7708 4.40417 17.625 4.625 17.425 4.825L4.25 18H0ZM12.475 5.525L11.775 4.8L13.2 6.225L12.475 5.525Z"
                        fill="#474747"
                      />
                    </svg>

                    <span>Edit</span>
                  </button>
                )}
              </MenuItem>

              {user?.role === 'OWNER' && (
                <MenuItem>
                  {() => (
                    <button
                      onClick={() => setIsInviteManagerModalOpen(true)}
                      className={`w-full text-sm md:text-base text-left p-2 md:p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                    >
                      <svg
                        width="24"
                        height="16"
                        className="w-5 h-5"
                        viewBox="0 0 24 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.5 7.95C12.9833 7.41667 13.3542 6.80833 13.6125 6.125C13.8708 5.44167 14 4.73333 14 4C14 3.26667 13.8708 2.55833 13.6125 1.875C13.3542 1.19167 12.9833 0.583333 12.5 0.05C13.5 0.183333 14.3333 0.625 15 1.375C15.6667 2.125 16 3 16 4C16 5 15.6667 5.875 15 6.625C14.3333 7.375 13.5 7.81667 12.5 7.95ZM18 16V13C18 12.4 17.8667 11.8292 17.6 11.2875C17.3333 10.7458 16.9833 10.2667 16.55 9.85C17.4 10.15 18.1875 10.5375 18.9125 11.0125C19.6375 11.4875 20 12.15 20 13V16H18ZM20 9V7H18V5H20V3H22V5H24V7H22V9H20ZM8 8C6.9 8 5.95833 7.60833 5.175 6.825C4.39167 6.04167 4 5.1 4 4C4 2.9 4.39167 1.95833 5.175 1.175C5.95833 0.391667 6.9 0 8 0C9.1 0 10.0417 0.391667 10.825 1.175C11.6083 1.95833 12 2.9 12 4C12 5.1 11.6083 6.04167 10.825 6.825C10.0417 7.60833 9.1 8 8 8ZM0 16V13.2C0 12.6333 0.145833 12.1125 0.4375 11.6375C0.729167 11.1625 1.11667 10.8 1.6 10.55C2.63333 10.0333 3.68333 9.64583 4.75 9.3875C5.81667 9.12917 6.9 9 8 9C9.1 9 10.1833 9.12917 11.25 9.3875C12.3167 9.64583 13.3667 10.0333 14.4 10.55C14.8833 10.8 15.2708 11.1625 15.5625 11.6375C15.8542 12.1125 16 12.6333 16 13.2V16H0ZM8 6C8.55 6 9.02083 5.80417 9.4125 5.4125C9.80417 5.02083 10 4.55 10 4C10 3.45 9.80417 2.97917 9.4125 2.5875C9.02083 2.19583 8.55 2 8 2C7.45 2 6.97917 2.19583 6.5875 2.5875C6.19583 2.97917 6 3.45 6 4C6 4.55 6.19583 5.02083 6.5875 5.4125C6.97917 5.80417 7.45 6 8 6ZM2 14H14V13.2C14 13.0167 13.9542 12.85 13.8625 12.7C13.7708 12.55 13.65 12.4333 13.5 12.35C12.6 11.9 11.6917 11.5625 10.775 11.3375C9.85833 11.1125 8.93333 11 8 11C7.06667 11 6.14167 11.1125 5.225 11.3375C4.30833 11.5625 3.4 11.9 2.5 12.35C2.35 12.4333 2.22917 12.55 2.1375 12.7C2.04583 12.85 2 13.0167 2 13.2V14Z"
                          fill="#474747"
                        />
                      </svg>

                      <span>Add Manager</span>
                    </button>
                  )}
                </MenuItem>
              )}
              <MenuItem>
                {() => (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    className={`w-full text-sm md:text-base text-left p-2 md:p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="16"
                      height="18"
                      className="w-5 h-5"
                      viewBox="0 0 16 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM13 3H3V16H13V3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
                        fill="#E03A3D"
                      />
                    </svg>

                    <span>Delete House</span>
                  </button>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </Transition>
      </Menu>

      {/* Delete Modal */}
      <DeleteHouseModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={(id) => {
          // Handle delete confirmation
          console.log('Delete house:', id);
          setIsDeleteModalOpen(false);
        }}
        house={house}
      />
      <InviteHomeManagerModal
        isOpen={isInviteManagerModalOpen}
        onClose={() => setIsInviteManagerModalOpen(false)}
        house={house}
      />
    </div>
  );
};
